'use client';
import { useEffect, useRef, useState } from 'react';
import { useResolvedAuthUser } from '@/contexts/resolved-auth-user-context';
import {
  withinS8StockBudget,
  S8_STOCK_RECOVERY_EVENT,
  s8StockRecoveryScope,
  s8StockHandles,
  recoverS8Stock,
  retireS8Stock,
  forgetS8StockHandle,
} from '@/lib/medications/s8-stock-client';
export function S8StockRecovery() {
  const actor = useResolvedAuthUser();
  const scope = actor?.id && actor.tenantId ? s8StockRecoveryScope(actor.id, actor.tenantId) : null;
  const [state, setState] = useState<{
    scope: string | null;
    keys: string[];
    committed: string[];
    message: string;
  }>({ scope: null, keys: [], committed: [], message: '' });
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const current = useRef(scope);
  current.current = scope;
  useEffect(() => {
    current.current = scope;
    setBusy(false);
    setAcknowledged(false);
    const load = () =>
      setState({ scope, keys: scope ? s8StockHandles(scope) : [], committed: [], message: '' });
    load();
    window.addEventListener(S8_STOCK_RECOVERY_EVENT, load);
    window.addEventListener('storage', load);
    return () => {
      current.current = null;
      window.removeEventListener(S8_STOCK_RECOVERY_EVENT, load);
      window.removeEventListener('storage', load);
    };
  }, [scope]);
  if (!scope || state.scope !== scope || !state.keys.length) return null;
  async function check() {
    if (!scope || busy) return;
    setBusy(true);
    const committed: string[] = [];
    let missing = false;
    let quarantined = false;
    try {
      for (const key of state.keys) {
        const result = await withinS8StockBudget(recoverS8Stock({ operation_id: key }));
        if (current.current !== scope) return;
        if (result === 'QUARANTINED') quarantined = true;
        if (result === 'QUARANTINED' || result === 'COMMITTED' || result === 'RETIRED')
          committed.push(key);
        else missing = true;
      }
      if (current.current === scope)
        setState((old) => ({
          ...old,
          committed,
          message: quarantined
            ? 'A dose assertion was retained for clinical review and an incident was raised; stock was not changed. Do not administer again. Keep any unresolved references.'
            : missing
              ? 'No committed receipt was observed for some commands. A delayed request may still commit. This does not prove failure or cancellation; keep the references and do not re-enter the same physical movement.'
              : 'The server confirms these commands committed or were durably retired. Read current stock and review physical movements before another entry.',
        }));
    } catch {
      if (current.current === scope)
        setState((old) => ({
          ...old,
          message:
            'The stock outcomes could not be verified. Keep the references and do not re-enter the same physical movement.',
        }));
    } finally {
      if (current.current === scope) setBusy(false);
    }
  }
  async function retire() {
    if (!scope || busy || !acknowledged) return;
    setBusy(true);
    try {
      for (const key of state.keys) {
        await withinS8StockBudget(retireS8Stock(key));
        if (current.current !== scope) return;
        forgetS8StockHandle(scope, key);
      }
    } catch {
      if (current.current === scope)
        setState((old) => ({
          ...old,
          message:
            'Some retirement outcomes could not be verified. Their references remain; check receipts before another stock entry.',
        }));
    } finally {
      if (current.current === scope) setBusy(false);
    }
  }
  return (
    <section
      aria-label="Prior S8 stock commands"
      className="my-3 rounded border border-status-warning-border p-3 text-body"
    >
      <p>
        A prior S8 stock command needs outcome verification. No clinical payload is stored in this
        browser recovery reference.
      </p>
      <button type="button" disabled={busy} onClick={() => void check()}>
        Check prior S8 stock receipts
      </button>
      <label>
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(event) => setAcknowledged(event.target.checked)}
        />
        I understand retirement only fences an uncommitted documentation request. I will review
        physical stock and the current balance before new documentation and will not repeat a
        physical administration to retry recording it.
      </label>
      <button type="button" disabled={busy || !acknowledged} onClick={() => void retire()}>
        Retire uncommitted stock requests or acknowledge committed receipts
      </button>
      {state.message && <p role="status">{state.message}</p>}
      {state.committed.length > 0 && (
        <button
          type="button"
          onClick={() => {
            for (const key of state.committed) forgetS8StockHandle(scope, key);
          }}
        >
          Acknowledge verified stock command outcomes
        </button>
      )}
    </section>
  );
}
